import { Box, Flex, HStack, Icon, Image, Tag, Text, VStack } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

const A4_HEIGHT = 1122;
const PAGE_PADDING_TOP = 40;
const PAGE_PADDING_BOTTOM = 48; // includes page number space + breathing room

interface TermsAndSignaturePageProps {
  proposal: any;
  user: any;
  isDownload: boolean;
}

export const TermsAndSignaturePage = ({
  proposal,
  user,
  isDownload,
}: TermsAndSignaturePageProps) => {
  const contentMeasureRef = useRef<HTMLDivElement>(null);
  const headerMeasureRef = useRef<HTMLDivElement>(null);
  const footerMeasureRef = useRef<HTMLDivElement>(null);

  const [pagesHtml, setPagesHtml] = useState<string[]>([]);

  const { user: reduxUser } = useSelector((state: { common: any }) => state.common);

  const showClientSignature =
    proposal?.status === "ACCEPTED" || proposal?.status === "DECLINED";

  const watermarkUrl = reduxUser?.ComapanyImageTwo;

  const sentDateText = useMemo(() => {
    try {
      if (!proposal?.sentDate) return "";
      return new Date(proposal?.sentDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "";
    }
  }, [proposal?.sentDate]);

  const reactionDateText = useMemo(() => {
    try {
      if (!proposal?.clientReactionDate) return "";
      return new Date(proposal?.clientReactionDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "";
    }
  }, [proposal?.clientReactionDate]);

  useEffect(() => {
    const contentNode = contentMeasureRef.current;
    if (!contentNode || !proposal?.terms?.value) {
      setPagesHtml([]);
      return;
    }

    // Measure header/footer heights (rendered in hidden container)
    const headerH = headerMeasureRef.current?.offsetHeight || 0;
    const footerH = footerMeasureRef.current?.offsetHeight || 0;

    const baseAvailable = A4_HEIGHT - PAGE_PADDING_TOP - PAGE_PADDING_BOTTOM;
    const availableFirst = Math.max(200, baseAvailable - headerH);
    const availableLast = Math.max(200, baseAvailable - footerH);

    const children = Array.from(contentNode.children) as HTMLElement[];

    const pages: string[] = [];
    let current: HTMLElement[] = [];
    let currentHeight = 0;
    let pageIndex = 0;

    const getAvailable = (idx: number) => {
      if (idx === 0) return availableFirst;
      return baseAvailable;
    };

    const flush = () => {
      if (current.length > 0) {
        pages.push(current.map((el) => el.outerHTML).join(""));
      }
      current = [];
      currentHeight = 0;
      pageIndex += 1;
    };

    for (const child of children) {
      const elHeight = child.offsetHeight;
      const available = getAvailable(pageIndex);

      if (currentHeight + elHeight > available && current.length > 0) {
        flush();
      }

      current.push(child.cloneNode(true) as HTMLElement);
      currentHeight += elHeight;
    }
    if (current.length > 0) flush();

    // Ensure last page leaves room for footer by moving blocks forward if needed
    if (pages.length > 0 && footerH > 0) {
      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.visibility = "hidden";
      tempContainer.style.width = "714px";
      tempContainer.style.pointerEvents = "none";
      document.body.appendChild(tempContainer);

      const fitsWithFooter = (html: string) => {
        tempContainer.innerHTML = html;
        return tempContainer.offsetHeight <= availableLast;
      };

      while (pages.length >= 2 && !fitsWithFooter(pages[pages.length - 1])) {
        const prevIndex = pages.length - 2;
        const prevWrap = document.createElement("div");
        prevWrap.innerHTML = pages[prevIndex];
        const prevKids = Array.from(prevWrap.children);
        if (prevKids.length === 0) break;

        const moved = prevKids.pop() as HTMLElement;
        const newPrev = prevKids.map((n) => (n as HTMLElement).outerHTML).join("");
        const newLast = `${moved.outerHTML}${pages[pages.length - 1]}`;

        pages[prevIndex] = newPrev;
        pages[pages.length - 1] = newLast;

        if (!pages[prevIndex]) {
          pages.splice(prevIndex, 1);
        }
      }

      document.body.removeChild(tempContainer);
    }

    setPagesHtml(pages.filter(Boolean));
  }, [proposal?.terms?.value, showClientSignature, sentDateText, reactionDateText]);

  if (!proposal?.terms?.value) return null;

  const termsSx = {
    "& p": { mb: 3, lineHeight: "1.5", fontSize: "13px" },
    "& ul, & ol": { pl: 6, mb: 3 },
    "& li": { mb: 1.5, fontSize: "13px", lineHeight: "1.5" },
    "& strong": { color: "gray.800", fontWeight: "700" },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      fontWeight: "700",
      color: "gray.900",
      mb: 2,
      mt: 3,
      lineHeight: "1.2",
    },
  } as const;

  const Header = () => (
    <Box px={10} pt={10} pb={4}>
      <Flex align="center" gap={3} mb={6}>
        <Box
          bgGradient="linear(to-br, orange.400, orange.600)"
          p={3}
          borderRadius="xl"
          boxShadow="md"
        >
          <Icon viewBox="0 0 24 24" boxSize={5} color="white" fill="currentColor">
            <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
          </Icon>
        </Box>
        <VStack align="start" spacing={0}>
          <Text fontSize="xl" fontWeight="bold" color="gray.800">
            Terms & Conditions
          </Text>
          <Text fontSize="xs" color="gray.500">
            Agreement details and policies
          </Text>
        </VStack>
      </Flex>
    </Box>
  );

  const Footer = () => (
    <Box px={10} pb={12} pt={4} borderTop="2px solid" borderColor="gray.200">
      <Flex justify="space-between" align="flex-end" flexWrap="wrap" gap={4}>
        <VStack align="start" spacing={2}>
          <Text fontWeight="bold" fontSize="lg" color="gray.800">
            Thank you for your business!
          </Text>
          <Text fontWeight="bold" color="blue.600">
            {user?.companyName}
          </Text>
          <VStack align="start" spacing={1} fontSize="xs">
            <Text>
              <strong>Contact:</strong> {user?.Phone}
            </Text>
            <Text>
              <strong>Email:</strong> {user?.Email}
            </Text>
            {!!sentDateText && (
              <Text>
                <strong>Date:</strong> {sentDateText}
              </Text>
            )}
          </VStack>
        </VStack>

        {showClientSignature && (
          <VStack
            align="flex-end"
            spacing={3}
            bg={proposal?.status === "ACCEPTED" ? "green.50" : "red.50"}
            p={4}
            borderRadius="lg"
            border="2px solid"
            borderColor={proposal?.status === "ACCEPTED" ? "green.300" : "red.300"}
          >
            <Tag
              size="lg"
              colorScheme={proposal?.status === "ACCEPTED" ? "green" : "red"}
              borderRadius="full"
              fontWeight="700"
            >
              {proposal?.status}
            </Tag>

            {proposal?.clientNote && (
              <Box bg="white" p={2} borderRadius="md" border="1px solid" borderColor="gray.200">
                <Text fontSize="xs" fontWeight="600" mb={1}>
                  Client Note:
                </Text>
                <Text fontSize="xs" color="gray.700">
                  {proposal?.clientNote}
                </Text>
              </Box>
            )}

            {proposal?.Esign && (
              <Image
                src={proposal?.Esign}
                w="120px"
                h="auto"
                bg="white"
                p={2}
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
              />
            )}

            <VStack align="end" spacing={0} fontSize="xs">
              <Text fontWeight="bold" color="gray.800">
                {proposal?.name}
              </Text>
              <Text color="gray.600">Client Signature</Text>
              {!!reactionDateText && <Text color="gray.500">{reactionDateText}</Text>}
            </VStack>
          </VStack>
        )}
      </Flex>
    </Box>
  );

  // Hidden measurement nodes (must be in DOM to measure offsetHeight)
  const Measure = () => (
    <Box
      position="absolute"
      zIndex={-9999}
      visibility="hidden"
      w="714px"
      pointerEvents="none"
    >
      <Box ref={headerMeasureRef}>
        <Header />
      </Box>
      <Box ref={contentMeasureRef} sx={termsSx}>
        <Box dangerouslySetInnerHTML={{ __html: proposal?.terms?.value }} />
      </Box>
      <Box ref={footerMeasureRef}>
        <Footer />
      </Box>
    </Box>
  );

  const pagesToRender = pagesHtml.length > 0 ? pagesHtml : [""];

  return (
    <>
      <Measure />

      {pagesToRender.map((html, pageIndex) => {
        const total = pagesToRender.length;
        const isFirst = pageIndex === 0;
        const isLast = pageIndex === total - 1;

        return (
          <Box
            key={pageIndex}
            position="relative"
            w="794px"
            h="1122px"
            bg="white"
            boxShadow="sm"
            className="page"
            overflow="hidden"
          >
            {!!watermarkUrl && (
              <Image
                src={watermarkUrl}
                alt="Background"
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                w="70%"
                h="auto"
                objectFit="contain"
                opacity={0.08}
                zIndex={0}
                pointerEvents="none"
                crossOrigin="anonymous"
              />
            )}

            <Box position="relative" zIndex={1} h="full">
              <Flex direction="column" h="full">
                {isFirst && <Header />}

                <Box
                  flex={1}
                  px={10}
                  pt={isFirst ? 0 : 10}
                  pb={isLast ? 0 : 12}
                  overflow="hidden"
                  sx={termsSx}
                  dangerouslySetInnerHTML={{ __html: html }}
                />

                {isLast && <Footer />}
              </Flex>
            </Box>

            <Text
              position="absolute"
              bottom={3}
              right={6}
              fontSize="10px"
              color="gray.400"
              zIndex={2}
              fontWeight="500"
            >
              {5 + pageIndex}
            </Text>
          </Box>
        );
      })}
    </>
  );
};

export default TermsAndSignaturePage;
